import { ETH_FAKE_ADDRESS, WRAPPED_NATIVE_ADDRESSES } from '@/constants/addresses';
import { CHAIN_IDS } from '@/constants/chain';

import { ConfettiDisplayConfig } from '@/hooks/useConfetti';

import { ChainAddress } from '@/utils/models';
import { TokenInfo } from '@/utils/token.types';

/** Match criteria for transaction-specific confetti triggers. */
type TransactionConfettiMatchConfig = {
  /** Restricts the trigger to swaps where sell and buy tokens are on the same chain. */
  sameChainOnly?: boolean;
  /** Matches when either the sell or buy token belongs to one of these chain IDs. */
  chains?: number[];
  /** Matches when either the sell or buy token matches one of these token identities. */
  tokens?: TokenInfo[];
};

/** A custom transaction confetti rule. Earlier triggers take precedence. */
export type TransactionConfettiTriggerConfig = {
  id: string;
  enabled: boolean;
  match: TransactionConfettiMatchConfig;
  confetti: ConfettiDisplayConfig;
};

/** Global transaction confetti settings. */
type TransactionConfettiConfig = {
  default: boolean;
  triggers: TransactionConfettiTriggerConfig[];
};

export const transactionConfettiConfig: TransactionConfettiConfig = {
  default: true,
  triggers: [
    {
      id: 'monad-token-swap',
      enabled: true,
      match: {
        sameChainOnly: true,
        tokens: [
          { address: WRAPPED_NATIVE_ADDRESSES[CHAIN_IDS.MONAD], chainId: CHAIN_IDS.MONAD },
          { address: ETH_FAKE_ADDRESS, chainId: CHAIN_IDS.MONAD },
        ],
      },
      confetti: {
        variant: 'cannon',
        imageSrc: '/images/confetti/monad.png',
        colors: ['#6E54FF', '#8B7AFF', '#5444CC', '#A594FF', '#9D8DFF', '#B8ACFF', '#fff'],
      },
    },
    {
      id: 'backpack-token-swap',
      enabled: true,
      match: {
        tokens: [
          {
            address: 'BPxxfRCXkUVhig4HS1Lh7kZqV6SPJhzfEk4x6fVBjPCy' as ChainAddress,
            chainId: CHAIN_IDS.SOLANA,
          },
        ],
      },
      confetti: {
        variant: 'cannon',
        imageSrc: '/images/confetti/backpack.png',
        imageWidth: 48,
        imageHeight: 70,
        colors: ['#FF69B4', '#87CEEB', '#DDA0DD', '#FFB6C1', '#ADD8E6', '#E6E6FA'],
      },
    },
    {
      id: 'token-cashcat',
      enabled: true,
      match: {
        tokens: [
          {
            address: '0x020bfc650a365f8bb26819deaabf3e21291018b4',
            chainId: CHAIN_IDS.ROBINHOOD,
          },
        ],
      },
      confetti: {
        variant: 'cannon',
        imageSrc: '/images/confetti/cashcat.png',
        colors: ['#CBFE00', '#D8FF33', '#E2FF66', '#B6E600', '#8DB300', '#1C190B'],
      },
    },
    {
      id: 'token-wishbone',
      enabled: true,
      match: {
        tokens: [
          {
            address: '0x77581054581b9c525e7dd7a0155de43867532d03',
            chainId: CHAIN_IDS.ROBINHOOD,
          },
        ],
      },
      confetti: {
        variant: 'cannon',
        imageSrc: '/images/confetti/wishbone.png',
        colors: ['#CBFE00', '#D8FF33', '#E2FF66', '#B6E600', '#8DB300', '#1C190B'],
      },
    },
    {
      id: 'token-rwog',
      enabled: true,
      match: {
        tokens: [
          {
            address: '0x49a947189125330f1658c65ce30882956bc9dbda',
            chainId: CHAIN_IDS.ROBINHOOD,
          },
        ],
      },
      confetti: {
        variant: 'cannon',
        imageSrc: '/images/confetti/rwog.png',
        colors: ['#CBFE00', '#D8FF33', '#E2FF66', '#B6E600', '#8DB300', '#1C190B'],
      },
    },
  ],
};
